import { Loader2, AlertCircle, Download as DownloadIcon } from 'lucide-react';
import { Button } from '@ai-tutor/ui';

/** Centered spinner for source content that's still loading. */
export function ViewerLoading() {
  return (
    <div className='flex h-full items-center justify-center'>
      <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
    </div>
  );
}

/** Inline error state - non-fatal; doesn't block the rest of the lesson. */
export function ViewerError({
  message,
  detail,
  onRetry,
}: {
  message: string;
  detail?: string;
  onRetry?: () => void;
}) {
  return (
    <div className='flex h-full flex-col items-center justify-center gap-2 p-6 text-center'>
      <div className='mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10'>
        <AlertCircle className='h-6 w-6 text-destructive-emphasis' />
      </div>
      <p className='text-sm font-medium text-foreground'>{message}</p>
      {detail && (
        <p className='max-w-sm text-xs text-muted-foreground'>{detail}</p>
      )}
      {onRetry && (
        <Button
          size='sm'
          variant='outline'
          onClick={onRetry}
          className='tap-target mt-2'
        >
          Retry
        </Button>
      )}
    </div>
  );
}

/**
 * Friendly fallback for formats the layered viewer can't render in-page.
 * Surfaces the filename + a Download CTA so the user has a path forward
 * even when we don't have a preview.
 */
export function UnsupportedPreview({
  filename,
  onDownload,
}: {
  filename: string;
  onDownload?: () => void;
}) {
  return (
    <div className='flex h-full flex-col items-center justify-center gap-3 p-6 text-center'>
      <div className='flex h-14 w-14 items-center justify-center rounded-full border border-border/60'>
        <DownloadIcon className='h-6 w-6 text-muted-foreground' />
      </div>
      <p className='text-sm font-medium text-foreground'>
        Preview not available
      </p>
      <p className='max-w-sm text-xs text-muted-foreground'>
        We can't render <span className='font-medium'>{filename}</span> in the
        browser yet. Download to open it locally.
      </p>
      {onDownload && (
        <Button size='sm' onClick={onDownload} className='tap-target mt-2'>
          <DownloadIcon className='mr-2 h-3.5 w-3.5' />
          Download
        </Button>
      )}
    </div>
  );
}
